import React, { useEffect, useState } from 'react';
import { dlpService } from '../../services/dlp.service';
import { SteelReportTable } from './SteelReportTable';
import { mapMediaTimeSlotLabel } from '../../lib/dlpSteelTable';

interface Props {
  period: string;
  company: string;
}

/** IPT-621 — 제강 계열은 3개월 미사용 USB 내역을 확인할 필요가 없어 화면에서 제외한다 */
const EXCLUDED_TABLE_NAMES = new Set(['3개월 미사용 USB 내역']);

const MEDIA_TIME_SLOT_TABLE_NAME = '매체 이동 시간대별 현황';

/** IPT-621 후속 — 표별 컬럼 순서 강제 (운영 캐시 JSONB 왕복 시 원본 API 키 순서 미보존 대응) */
const COLUMN_ORDER: Record<string, string[]> = {
  [MEDIA_TIME_SLOT_TABLE_NAME]: ['구분', '파일건수', '비율'],
};

/** IPT-621 후속 — API가 순수 숫자만 반환하는 비율에 % 표시를 붙인다 */
function formatPercent(value: string | undefined): string | undefined {
  if (value === undefined || value.endsWith('%')) return value;
  return `${value}%`;
}

function applyMediaControlRules(tableName: string, rows: Record<string, string>[]): Record<string, string>[] {
  if (tableName !== MEDIA_TIME_SLOT_TABLE_NAME) return rows;
  return rows.map((row) => ({
    ...row,
    구분: mapMediaTimeSlotLabel(row['구분']),
    ...(row['비율'] !== undefined ? { 비율: formatPercent(row['비율'])! } : {}),
  }));
}

/** 세아제강 계열 DLP — 매체 제어 탭 (IPT-441, Privacy-i API report_type=copy) */
export const SteelMediaControlTab: React.FC<Props> = ({ period, company }) => {
  const [tables, setTables] = useState<Record<string, Record<string, string>[]>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!period || !company) return;
    setIsLoading(true);
    dlpService
      .getSteelReport(period, company)
      .then((res) => setTables(res.reports.copy))
      .catch(() => setTables({}))
      .finally(() => setIsLoading(false));
  }, [period, company]);

  if (isLoading) return <div>로딩 중...</div>;

  return (
    <div className="tab-panel steel-media-control-tab">
      <h2>매체 제어</h2>
      {Object.entries(tables)
        .filter(([tableName]) => !EXCLUDED_TABLE_NAMES.has(tableName))
        .map(([tableName, rows]) => (
          <SteelReportTable
            key={tableName}
            title={tableName}
            rows={applyMediaControlRules(tableName, rows)}
            columnOrder={COLUMN_ORDER[tableName]}
          />
        ))}
      {Object.keys(tables).length === 0 && <div>데이터가 없습니다.</div>}
    </div>
  );
};
